import { proposalQueryKeys } from "@platform/mpx-quotes-grid";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";

import type { RfpBenefitPlan } from "@platform/btx-plan-designer";
import type { PlaceholderMaterializeTarget } from "@platform/mpx-quotes-grid";
import type { AxiosInstance } from "axios";

/**
 * Plan-level fields (siblings of `benefits`) the analyst can edit on a quote.
 * The backend persists these as proposal-plan columns. `rateCap` maps from the
 * FE's `rateCap` field.
 */
export interface ProposalPlanLevelFields {
  fundingType?: RfpBenefitPlan["fundingType"];
  participationRequirements?: RfpBenefitPlan["participationRequirements"];
  rateGuarantee?: RfpBenefitPlan["rateGuarantee"];
  rateCap?: RfpBenefitPlan["rateCap"];
  eligibilityClasses?: RfpBenefitPlan["eligibilityClasses"];
  optionName?: RfpBenefitPlan["optionName"];
  name?: RfpBenefitPlan["name"];
}

/** Request body for POST /btx/v1/rfps/:id/proposals (materialize a placeholder). */
interface MaterializeProposalRequest {
  carrierId: string;
  /**
   * Display name resolved for carrierId. BTX has no carrier directory, so
   * without this the proposal's carrier_name persists as the slug. Omitted
   * when unresolved (equal to the slug) — BTX falls back to carrierId anyway.
   */
  carrierName?: string;
  origin: "analyst";
  materialize: {
    rfpBenefitPlanGroupId: string;
    planPosition: number;
    // Optional: a plan-level-only edit (e.g. funding type) carries no benefit values.
    benefits?: RfpBenefitPlan["benefits"];
    footnote?: string;
  } & ProposalPlanLevelFields;
}

/** The slice of the POST response needed to find the newly materialized plan's real id. */
interface MaterializeProposalResponse {
  coverages: {
    planGroups: {
      rfpBenefitPlanGroupId?: string;
      plans: { id: string; position?: number }[];
    }[];
  }[];
}

// The materialize endpoint returns the whole proposal tree rather than the
// single created plan, so the real id is found by matching the group/position
// the caller asked to materialize.
const findMaterializedPlanId = (
  response: MaterializeProposalResponse,
  target: PlaceholderMaterializeTarget,
): string | undefined => {
  for (const coverage of response.coverages) {
    for (const planGroup of coverage.planGroups) {
      if (planGroup.rfpBenefitPlanGroupId !== target.rfpBenefitPlanGroupId) {
        continue;
      }
      const plan = planGroup.plans.find(
        (p) => p.position === target.planPosition,
      );
      if (plan) return plan.id;
    }
  }
  return undefined;
};

const materializeKey = (target: PlaceholderMaterializeTarget): string =>
  `${target.carrierId}:${target.rfpBenefitPlanGroupId}`;

const postMaterializeProposal = async (
  axiosInstance: AxiosInstance,
  rfpId: string,
  target: PlaceholderMaterializeTarget,
  benefits: RfpBenefitPlan["benefits"],
  planFields: ProposalPlanLevelFields,
  footnote: string | undefined,
): Promise<MaterializeProposalResponse> => {
  const hasResolvedName =
    !!target.carrierName && target.carrierName !== target.carrierId;
  const body: MaterializeProposalRequest = {
    carrierId: target.carrierId,
    ...(hasResolvedName ? { carrierName: target.carrierName } : {}),
    origin: "analyst",
    materialize: {
      rfpBenefitPlanGroupId: target.rfpBenefitPlanGroupId,
      planPosition: target.planPosition,
      benefits,
      ...(footnote !== undefined ? { footnote } : {}),
      ...planFields,
    },
  };
  const response = await axiosInstance.post<MaterializeProposalResponse>(
    `btx/v1/rfps/${rfpId}/proposals`,
    body,
  );
  return response.data;
};

interface MaterializeVars {
  target: PlaceholderMaterializeTarget;
  benefits: RfpBenefitPlan["benefits"];
  planFields: ProposalPlanLevelFields;
  footnote?: string;
  /** Invoked with the newly materialized plan's real id once the proposal is created. */
  onMaterialized?: (planId: string) => void;
}

interface UseMaterializeProposalMutationOptions {
  rfpId: string;
  axiosInstance: AxiosInstance;
  onSaveError?: (err: Error) => void;
}

/**
 * Materializes a synthesized "awaiting quote" placeholder column into a real
 * origin=analyst proposal carrying the analyst's edited benefit (ADR-024).
 *
 * The materialize endpoint always appends a new plan group (it is not idempotent
 * per rfpBenefitPlanGroupId), so we guard against firing twice for the same
 * (carrier, group): a key stays in flight until the proposals refetch lands and
 * the placeholder column has been replaced by the real one. Without this, a
 * second edit during the refetch window would create a duplicate group.
 */
export const useMaterializeProposalMutation = ({
  rfpId,
  axiosInstance,
  onSaveError,
}: UseMaterializeProposalMutationOptions): {
  materialize: (
    target: PlaceholderMaterializeTarget,
    benefits: RfpBenefitPlan["benefits"],
    planFields: ProposalPlanLevelFields,
    footnote?: string,
    onMaterialized?: (planId: string) => void,
  ) => void;
} => {
  const queryClient = useQueryClient();
  const queryKey = proposalQueryKeys.all(rfpId);
  const inFlightKeys = useRef(new Set<string>());

  const { mutate } = useMutation({
    mutationFn: ({ target, benefits, planFields, footnote }: MaterializeVars) =>
      postMaterializeProposal(
        axiosInstance,
        rfpId,
        target,
        benefits,
        planFields,
        footnote,
      ),
    onSuccess: (data, { target, onMaterialized }: MaterializeVars) => {
      const planId = findMaterializedPlanId(data, target);
      if (planId) {
        onMaterialized?.(planId);
      } else if (onMaterialized) {
        onSaveError?.(
          new Error(
            `Materialize response did not include a plan for group ${target.rfpBenefitPlanGroupId} position ${target.planPosition}`,
          ),
        );
      }
      // Await the refetch so the placeholder is replaced before the in-flight
      // guard is released (see onSettled) — otherwise a quick second edit would
      // materialize a duplicate group.
      return queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: Error) => {
      onSaveError?.(err);
    },
    onSettled: (_data, _err, { target }: MaterializeVars) => {
      inFlightKeys.current.delete(materializeKey(target));
    },
  });

  const materialize = useCallback(
    (
      target: PlaceholderMaterializeTarget,
      benefits: RfpBenefitPlan["benefits"],
      planFields: ProposalPlanLevelFields,
      footnote?: string,
      onMaterialized?: (planId: string) => void,
    ) => {
      const key = materializeKey(target);
      if (inFlightKeys.current.has(key)) {
        // A materialize for this (carrier, group) is already in flight, so this
        // call's onMaterialized/retry would otherwise be silently dropped.
        onSaveError?.(
          new Error(
            `Materialize already in flight for group ${target.rfpBenefitPlanGroupId}`,
          ),
        );
        return;
      }
      inFlightKeys.current.add(key);
      mutate({ target, benefits, planFields, footnote, onMaterialized });
    },
    [mutate, onSaveError],
  );

  return { materialize };
};
